import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import styled, { css } from "styled-components";
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from "@headlessui/react";
import { Row, Col } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClock,
  faUser,
  faPen,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { api } from "../App";
import {
  fetchEstimatedTimeCustomersSpend,
  createEstimatedTimeCustomersSpend,
  patchEstimatedTimeCustomersSpend,
  fetchHoursEach,
} from "../actions/restaurantActions";
import Loader from "./Loader";
import DropDownButton from "./DropDownButton";
import DropDownButtonTime from "./DropDownButtonTime";
import { CustomModal } from "./CustomModal";
import { ModernButton } from "./DefaultImports";
import { ConfirmationModal } from "./ConfirmationModal";
import { LuUsers, LuClock3, LuChevronDown, LuPlus, LuCheck } from "react-icons/lu";

const EstimatedTimePage = ({ restaurant_ids }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const restaurant_id = restaurant_ids;
  const options = useMemo(() => Array.from({ length: 100 }, (_, i) => i + 1), []);

  const [editingItem, setEditingItem] = useState(false);
  const [peopleNumer, setPeopleNumer] = useState(0);
  const [selectedTime, setSelectedTime] = useState("00:30");
  const [itemId, setItemId] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [newPeople, setNewPeople] = useState("");
  const [newDuration, setNewDuration] = useState("00:30");
  const [isAdding, setIsAdding] = useState(false);
  const [createFeedback, setCreateFeedback] = useState("");
  const addingRef = useRef(false);
  const peopleInputRef = useRef(null);

  const { estimatedTime } = useSelector(
    (state) => state.estimatedTimeCustomersSpend,
  );

  const timeList = useMemo(() => {
    const result = [];
    const time = new Date(0, 0, 0, 0, 0, 0, 0);
    const end = new Date(0, 0, 0, 10, 0, 0, 0);
    while (time <= end) {
      const hour = time.getHours().toString().padStart(2, "0");
      const minutes = time.getMinutes().toString().padStart(2, "0");
      result.push(`${hour}:${minutes}`);
      time.setMinutes(time.getMinutes() + 15);
    }
    return result;
  }, []);

  useEffect(() => {
    if (!restaurant_id) return;
    dispatch(fetchEstimatedTimeCustomersSpend(restaurant_id));
    dispatch(fetchHoursEach(restaurant_id));
  }, [dispatch, restaurant_id]);

  const triggerDelete = (id) => {
    setDeleteItemId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(
        `/restaurants/${restaurant_id}/estimatedtime/${deleteItemId}/`,
      );
      dispatch(fetchEstimatedTimeCustomersSpend(restaurant_id));
    } catch (error) {
      console.error(error);
    }
    setShowDeleteModal(false);
    setDeleteItemId(null);
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    if (addingRef.current || !restaurant_id) return;
    const numberOfPeople = Number(newPeople);
    if (!Number.isInteger(numberOfPeople) || numberOfPeople < 1 || numberOfPeople > 100 || !timeList.includes(newDuration)) {
      setCreateFeedback("invalid");
      return;
    }
    addingRef.current = true;
    setIsAdding(true);
    setCreateFeedback("");
    try {
      const result = await dispatch(
        createEstimatedTimeCustomersSpend(restaurant_id, {
          restaurant: restaurant_id,
          number_of_people: numberOfPeople,
          estimated_time: newDuration,
        }),
      );
      if (result?.success) {
        setNewPeople("");
        setCreateFeedback("added");
        peopleInputRef.current?.focus();
      } else {
        setCreateFeedback("failed");
      }
    } catch {
      setCreateFeedback("failed");
    } finally {
      addingRef.current = false;
      setIsAdding(false);
    }
  };

  const handleOpenEditModal = (people, time, id) => {
    setItemId(id);
    setPeopleNumer(people);
    setSelectedTime(time);
    setEditingItem(true);
  };

  const handleEditSave = () => {
    dispatch(
      patchEstimatedTimeCustomersSpend(restaurant_id, itemId, {
        number_of_people: peopleNumer,
        estimated_time: selectedTime,
      }),
    );
    setEditingItem(false);
  };

  const sortedEstimatedTimes = Array.isArray(estimatedTime)
    ? [...estimatedTime].sort(
        (a, b) => a.number_of_people - b.number_of_people,
      )
    : null;

  return (
    <AverageDurationCard>
      <AverageDurationHeader>
        <TitleGroup>
          <HeaderIcon>
            <FontAwesomeIcon icon={faClock} />
          </HeaderIcon>
          <div>
            <SectionTitle>
              {t(
                "ui.components.estimatedTimeCustomersSpend.duracaoMediaDasRefeicoes",
              )}
            </SectionTitle>
          </div>
        </TitleGroup>
      </AverageDurationHeader>

      <ColumnLabels>
        <span>{t("ui.components.estimatedTimeCustomersSpend.tamanhoDoGrupo")}</span>
        <span>{t("ui.components.estimatedTimeCustomersSpend.duracaoMedia")}</span>
        <span />
      </ColumnLabels>

      <InlineCreateForm onSubmit={handleCreate} aria-label={t("ui.components.estimatedTimeCustomersSpend.adicionarDuracaoMedia")} aria-busy={isAdding}>
        <EntryField>
          <EntryLabel>{t("ui.components.estimatedTimeCustomersSpend.tamanhoDoGrupo")}</EntryLabel>
          <InputShell>
            <LuUsers aria-hidden="true" />
            <GroupInput ref={peopleInputRef} type="number" inputMode="numeric" min="1" max="100" step="1" required placeholder="2" value={newPeople} readOnly={isAdding} onChange={event => { setNewPeople(event.target.value); setCreateFeedback(""); }} />
          </InputShell>
        </EntryField>
        <EntryField as="div">
          <EntryLabel>{t("ui.components.estimatedTimeCustomersSpend.duracaoMedia")}</EntryLabel>
          <Listbox value={newDuration} disabled={isAdding} onChange={value => { setNewDuration(value); setCreateFeedback(""); }}>
            <DurationTrigger type="button" aria-label={t("ui.components.estimatedTimeCustomersSpend.duracaoMedia")}>
              <LuClock3 aria-hidden="true" />
              <span>{newDuration}</span>
              <LuChevronDown aria-hidden="true" />
            </DurationTrigger>
            <DurationMenu anchor={{ to: "bottom start", gap: 6, padding: 12 }} modal={false}>
              {timeList.map(time => (
                <DurationOption key={time} value={time}>
                  {({ selected }) => <><span>{time}</span>{selected && <LuCheck aria-hidden="true" />}</>}
                </DurationOption>
              ))}
            </DurationMenu>
          </Listbox>
        </EntryField>
        <AddButton type="submit" disabled={isAdding || !restaurant_id}>
          <LuPlus aria-hidden="true" />
          {isAdding ? t("averageDurationForm.adding") : t("ui.components.estimatedTimeCustomersSpend.adicionar")}
        </AddButton>
      </InlineCreateForm>
      {createFeedback && <FormFeedback role={createFeedback === "added" ? "status" : "alert"} $error={createFeedback !== "added"}>{t(`averageDurationForm.${createFeedback}`)}</FormFeedback>}

      <DurationList>
        {sortedEstimatedTimes ? (
          sortedEstimatedTimes.length > 0 ? (
            sortedEstimatedTimes.map((item) => (
              <DurationRow key={item.id}>
                <Metric>
                  <MetricIcon>
                    <FontAwesomeIcon icon={faUser} />
                  </MetricIcon>
                  <MetricValue>{item.number_of_people}</MetricValue>
                </Metric>
                <Metric>
                  <MetricIcon>
                    <FontAwesomeIcon icon={faClock} />
                  </MetricIcon>
                  <MetricValue>{item.estimated_time.slice(0, 5)}</MetricValue>
                </Metric>
                <RowActions>
                  <IconButton
                    type="button"
                    aria-label={t("ui.components.estimatedTimeCustomersSpend.editarDuracaoMedia")}
                    onClick={() =>
                      handleOpenEditModal(
                        item.number_of_people,
                        item.estimated_time.slice(0, 5),
                        item.id,
                      )
                    }
                  >
                    <FontAwesomeIcon icon={faPen} />
                  </IconButton>
                  <IconButton
                    type="button"
                    aria-label={t("ui.components.estimatedTimeCustomersSpend.eliminarDuracaoMedia")}
                    $danger
                    onClick={() => triggerDelete(item.id)}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </IconButton>
                </RowActions>
              </DurationRow>
            ))
          ) : (
            <EmptyState>
              {t("averageDurationForm.empty")}
            </EmptyState>
          )
        ) : (
          <Loader />
        )}
      </DurationList>

      <CustomModal
        show={editingItem === true}
        onHide={() => setEditingItem(false)}
        title={t("ui.components.estimatedTimeCustomersSpend.editarDuracaoMedia")}
        centered={false}
        footer={
          <>
            <ModernButton color="#99B0AD" onClick={() => setEditingItem(false)}>
              {t("ui.components.estimatedTimeCustomersSpend.cancelar")}
            </ModernButton>
            <ModernButton color="#ee754c" onClick={handleEditSave}>
              {t("ui.components.estimatedTimeCustomersSpend.gravar")}
            </ModernButton>
          </>
        }
      >
        <Row className="g-4 mb-3">
          <Col xs={12} md={6}>
            <StyledH5>
              <FontAwesomeIcon icon={faUser} />
              {t("ui.components.estimatedTimeCustomersSpend.pessoas2")}
            </StyledH5>
            <DropDownButton
              options={options}
              handleOptionChange={setPeopleNumer}
              initialOption={peopleNumer}
            />
          </Col>
          <Col xs={12} md={6}>
            <StyledH5>
              <FontAwesomeIcon icon={faClock} />
              {t("ui.components.estimatedTimeCustomersSpend.tempo2")}
            </StyledH5>
            <DropDownButtonTime
              options={timeList}
              handleOptionChange={setSelectedTime}
              initialOption={selectedTime}
            />
          </Col>
        </Row>
      </CustomModal>

      <ConfirmationModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        onCancel={() => setShowDeleteModal(false)}
        title={t(
          "ui.components.estimatedTimeCustomersSpend.eliminarDuracaoMedia",
        )}
        body={t("ui.i18nCleanup.deleteAverageDurationConfirm")}
        confirmLabel={t("common.delete")}
        cancelLabel={t("ui.components.estimatedTimeCustomersSpend.cancelar")}
        confirmColor="#ee754c"
        cancelColor="#99B0AD"
        centered={false}
        topSpacing="100px auto"
      />
    </AverageDurationCard>
  );
};

export default EstimatedTimePage;

const AverageDurationCard = styled.section`
  margin-top: 10px;
  background: #ffffff;
  border: 1px solid #edf0ef;
  border-radius: 12px;
  padding: 22px;
  box-shadow: 0 2px 8px rgba(67, 48, 52, 0.08);
  min-width: 0;

  @media (max-width: 600px) {
    padding: 14px 12px;
  }
`;
const AverageDurationHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding-bottom: 16px;
  margin-bottom: 14px;
  border-bottom: 1px solid #eef1f0;

  @media (max-width: 600px) {
    align-items: stretch;
    flex-direction: column;
  }
`;
const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;
const HeaderIcon = styled.span`
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #f1f6f5;
  color: #799794;
  flex: 0 0 auto;
`;
const SectionTitle = styled.div`
  color: #52625f;
  font-size: 17px;
  font-weight: 700;
`;
const AddButton = styled.button`
  min-height: 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 7px 12px;
  border: 0;
  border-radius: 8px;
  background: #ee754c;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #dc6842;
  }
  &:disabled { opacity: 0.65; cursor: wait; }
  &:focus-visible { outline: 3px solid #f9d7ca; outline-offset: 2px; }
  svg { width: 17px; height: 17px; flex-shrink: 0; }
`;
const ColumnLabels = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) 128px;
  gap: 12px;
  padding: 0 12px 8px;
  color: #87938f;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;

  @media (max-width: 600px) {
    display: none;
  }
`;
const InlineCreateForm = styled.form`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) 128px;
  gap: 12px;
  align-items: end;
  padding: 12px;
  margin-bottom: 12px;
  border-radius: 10px;
  background: #f5f8f7;
  @media (max-width: 600px) {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 10px;
    padding: 10px;
    > button { grid-column: 1 / -1; }
  }
`;
const EntryField = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  margin: 0;
`;
const EntryLabel = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  overflow: hidden;
  clip-path: inset(50%);
  @media (max-width: 600px) {
    position: static;
    width: auto;
    height: auto;
    clip-path: none;
    color: #657a75;
    font-size: 11px;
    font-weight: 600;
  }
`;
const fieldSurface = css`
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  min-height: 42px;
  border: 1px solid #dce6e3;
  border-radius: 8px;
  padding: 0 12px;
  background: white;
  transition: border-color 120ms, box-shadow 120ms;
  > svg { width: 17px; height: 17px; flex-shrink: 0; color: #78938c; }
  &:hover { border-color: #b7cbc4; }
  &:focus-within {
    border-color: #7f9e94;
    box-shadow: 0 0 0 2px rgba(127, 158, 148, 0.14);
  }
`;
const InputShell = styled.span`${fieldSurface}`;
const GroupInput = styled.input`
  min-width: 0;
  width: 100%;
  height: 40px;
  border: 0;
  outline: 0;
  background: transparent;
  color: #354541;
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  padding: 0;
  /* The surrounding field owns the focus ring, not the inner input. */
  &&, &&:focus, &&:focus-visible {
    border: 0;
    outline: none;
    box-shadow: none;
  }
  &::placeholder { color: #a2b0ac; font-weight: 400; }
  @media (max-width: 600px) { font-size: 16px; }
`;
const DurationTrigger = styled(ListboxButton)`
  ${fieldSurface}
  width: 100%;
  height: 42px;
  color: #354541;
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  text-align: left;
  cursor: pointer;
  > span { flex: 1; }
  > svg:last-child { width: 15px; height: 15px; transition: transform 120ms; }
  &[data-open] > svg:last-child { transform: rotate(180deg); }
  &&:focus, &&:focus-visible, &[data-open] {
    outline: none;
    border-color: #7f9e94;
    box-shadow: 0 0 0 2px rgba(127, 158, 148, 0.14);
  }
  &:disabled { opacity: 0.65; cursor: wait; }
  @media (max-width: 600px) { font-size: 16px; }
`;
const DurationMenu = styled(ListboxOptions)`
  z-index: 1100;
  width: clamp(180px, var(--button-width), 280px);
  max-width: calc(100vw - 24px);
  max-height: 264px;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 6px;
  border: 1px solid #dce6e3;
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 10px 28px rgba(43, 66, 57, 0.12), 0 2px 5px rgba(43, 66, 57, 0.04);
  scrollbar-width: thin;
  scrollbar-color: #c2d1cb transparent;
  &&:focus, &&:focus-visible { outline: none; }
`;
const DurationOption = styled(ListboxOption)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 38px;
  padding: 8px 10px;
  border-radius: 7px;
  color: #465c54;
  font-size: 14px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  cursor: pointer;
  user-select: none;
  &[data-focus] { background: #f2f5f4; }
  &[data-selected] { background: #eaf2ee; color: #375c4e; font-weight: 650; }
  &[data-selected][data-focus] { background: #dfece5; }
  > svg { width: 16px; height: 16px; flex-shrink: 0; }
  @media (pointer: coarse) { min-height: 44px; }
`;
const FormFeedback = styled.p`
  color: ${({ $error }) => $error ? "#a53b3b" : "#426553"};
  font-size: 13px;
  margin: 0 0 12px;
`;
const DurationList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 440px;
  overflow-y: auto;
  padding-right: 3px;
`;
const DurationRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) 128px;
  gap: 12px;
  align-items: center;
  padding: 11px 12px;
  border: 1px solid #e6ecea;
  border-radius: 9px;
  background: #fcfdfc;

  &:hover {
    border-color: #cfdad8;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr 1fr auto;
    gap: 8px;
  }
`;
const Metric = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;
const MetricIcon = styled.span`
  color: #8ba6a2;
  width: 20px;
  display: inline-flex;
  justify-content: center;
`;
const MetricValue = styled.span`
  color: #354541;
  font-size: 14px;
  font-weight: 650;
`;
const RowActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 6px;
`;
const IconButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid ${({ $danger }) => ($danger ? "#f0d1d1" : "#dbe5e2")};
  background: ${({ $danger }) => ($danger ? "#fff7f7" : "#f5f9f8")};
  color: ${({ $danger }) => ($danger ? "#ba5d5d" : "#6f8f8b")};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    background: ${({ $danger }) => ($danger ? "#fff0f0" : "#edf5f3")};
  }
`;
const EmptyState = styled.div`
  border: 1px dashed #cad8d5;
  background: #fbfcfb;
  color: #7d8986;
  border-radius: 9px;
  padding: 20px;
  text-align: center;
  font-size: 13px;
`;
const StyledH5 = styled.h5`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 650;
  color: #657572;
  width: 100%;
  text-align: left;
  margin: 0 0 10px;
`;
